/**
 * Sales Ex-Tax Variance Report
 *
 * Per-aligned-sale LS<->Xero ex-tax variance across FPs for a chosen
 * period. Detail rows ordered by absolute variance, largest first.
 * FP Summary sheet rolls up the full period (no threshold) so reviewers
 * see the truthful per-FP net drift.
 */
import BaseReportPage, { ColumnDef, ColumnGroup } from './BaseReportPage'
import { buildRowInspectorLink } from '../../../../utils/urls'

const columns: ColumnDef[] = [
  { key: 'fp_code', label: 'FP Code', link: { builder: buildRowInspectorLink, external: false } },
  { key: 'franchise_name', label: 'Franchise' },
  { key: 'last_sync_at', label: 'Data Age', type: 'sync_status' },
  { key: 'fp_status', label: 'FP Status' },
  { key: 'market', label: 'Market' },
  { key: 'sale_date', label: 'Sale Date', type: 'business_date' },
  { key: 'invoice_number', label: 'Invoice #', link: { field: 'lightspeed_sale_link', system: 'lightspeed' } },
  { key: 'xero_kind', label: 'Xero Kind' },
  { key: 'ls_status', label: 'LS Status' },
  { key: 'xero_status', label: 'Xero Status' },
  { key: 'ls_voided', label: 'LS Voided', type: 'boolean' },
  { key: 'xero_voided', label: 'Xero Voided', type: 'boolean' },
  { key: 'ls_total_incl_tax', label: 'LS Total (Inc Tax)', type: 'currency' },
  { key: 'ls_ex_tax_signed', label: 'LS Ex-Tax', type: 'currency' },
  { key: 'xero_ex_tax_signed', label: 'Xero Ex-Tax', type: 'currency' },
  { key: 'ex_tax_variance', label: 'Variance ($)', type: 'currency' },
  { key: 'ex_tax_variance_pct', label: 'Variance (%)', type: 'percentage' },
  { key: 'direction', label: 'Direction' },
]

const columnGroups: ColumnGroup[] = [
  { label: null, columnKeys: ['fp_code', 'franchise_name', 'last_sync_at', 'fp_status', 'market'] },
  { label: 'Sale', columnKeys: ['sale_date', 'invoice_number', 'xero_kind', 'ls_status', 'xero_status', 'ls_voided', 'xero_voided'] },
  { label: 'Financial', columnKeys: ['ls_total_incl_tax', 'ls_ex_tax_signed', 'xero_ex_tax_signed', 'ex_tax_variance', 'ex_tax_variance_pct', 'direction'] },
]

export default function SalesExTaxVarianceReport() {
  return (
    <BaseReportPage
      reportType="sales_ex_tax_variance"
      title="Sales Ex-Tax Variance"
      description="Aligned sales where LS and Xero ex-tax magnitudes disagree above a threshold"
      filterConfig={{
        showFpSelector: true,
        showFpStatus: true,
        showCountry: true,
        showRegion: true,
        showDateRange: true,
        requireDateRange: true,
        defaultDateRange: 'previous_month',
        linkFpStatusToSearch: true,
        showMinVarianceAmount: true,
        defaultMinVarianceAmount: '1.00',
      }}
      columns={columns}
      columnGroups={columnGroups}
      defaultSortColumn="ex_tax_variance"
      defaultSortDirection="desc"
    />
  )
}
